import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useVirtualAppointmentSummaries } from '../../../virtual-appointments/useVirtualAppointmentSummaries';
import {
    formatSummaryTrigger,
    type VirtualAppointmentSummary
} from '@odo/shared-ui/clinical/virtualAppointments/summary';
import type { VirtualAppointmentDraft } from '../../../virtual-appointments/virtualAppointmentDraft';
import {
    VirtualAppointmentLoadError,
    VirtualAppointmentSplitSkeleton
} from '../../../virtual-appointments/VirtualAppointmentSplitStates';
import { VirtualAppointmentTypeTitle } from '@odo/shared-ui/clinical/virtualAppointments/VirtualAppointmentTypeTitle';
import { useVirtualAppointmentTypeLabels } from '../../../../hooks/useVirtualAppointments';
import type { VirtualAppointmentDto } from '../../../../services/virtualAppointments';
import {
    ClinicalVirtualAppointmentPlanner,
    type PlannerHistory
} from './ClinicalVirtualAppointmentPlanner';
import type { VirtualAppointmentPlansState } from './useVirtualAppointmentPlans';

const NO_SAVED: VirtualAppointmentDto[] = [];
const LOAD_ERROR_MESSAGE =
    "Couldn't load this document's VAs. Submitting is blocked so none are lost.";

interface ClinicalVirtualAppointmentsStepProps {
    patientId: string;
    planner: VirtualAppointmentPlansState;
    /** Shows a History tab beside the planned VAs (RDI). */
    history?: PlannerHistory;
}

function LegacyConversionNotice() {
    return (
        <div className="flex gap-2 text-sm text-yellow-800">
            <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-yellow-600" />
            <p>The previous VA note was moved into a new VA. Choose its type and trigger.</p>
        </div>
    );
}

interface RemovedItemProps {
    summary: VirtualAppointmentSummary;
}

function RemovedItem({ summary }: RemovedItemProps) {
    return (
        <li className="flex min-w-0 items-center gap-1">
            <VirtualAppointmentTypeTitle summary={summary} codeClassName="font-semibold" />
            <span className="shrink-0">
                · {formatSummaryTrigger(summary)} · {summary.team}
            </span>
        </li>
    );
}

interface RemovedElsewhereNoticeProps {
    removed: VirtualAppointmentDraft[];
}

function RemovedElsewhereNotice({ removed }: RemovedElsewhereNoticeProps) {
    const typeLabels = useVirtualAppointmentTypeLabels();
    const summaries = useVirtualAppointmentSummaries(NO_SAVED, removed, typeLabels);
    const one = summaries.length === 1;
    return (
        <div className="flex gap-2 min-w-0 text-sm text-yellow-800">
            <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-yellow-600" />
            <div className="min-w-0">
                <p>
                    {summaries.length} VA{one ? '' : 's'} in this draft {one ? 'was' : 'were'}{' '}
                    removed elsewhere and not restored. Add {one ? 'it' : 'them'} again if still
                    needed:
                </p>
                <ul className="mt-0.5 flex flex-col">
                    {summaries.map((summary) => (
                        <RemovedItem key={summary.key} summary={summary} />
                    ))}
                </ul>
            </div>
        </div>
    );
}

// Fills the wizard body: a slim document-level strip, then the full-height list/detail split.
export function ClinicalVirtualAppointmentsStep({
    patientId,
    planner,
    history
}: ClinicalVirtualAppointmentsStepProps) {
    const hasConversion = planner.legacyConversionPending;
    const hasRemoved = planner.removedElsewhere.length > 0;

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            {(hasConversion || hasRemoved) && (
                <div className="flex-shrink-0 flex flex-col gap-1.5 px-5 py-2.5 border-b border-gray-200 bg-gray-50">
                    {hasConversion && <LegacyConversionNotice />}
                    {hasRemoved && <RemovedElsewhereNotice removed={planner.removedElsewhere} />}
                </div>
            )}
            {planner.loadStatus === 'loading' && <VirtualAppointmentSplitSkeleton />}
            {planner.loadStatus === 'error' && (
                <VirtualAppointmentLoadError
                    message={LOAD_ERROR_MESSAGE}
                    onRetry={planner.retryLoad}
                />
            )}
            {planner.loadStatus === 'ready' && (
                <ClinicalVirtualAppointmentPlanner
                    patientId={patientId}
                    planner={planner}
                    history={history}
                />
            )}
        </div>
    );
}
